// AggregatesPanel.jsx
import { useEffect, useState } from "react";

export default function AggregatesPanel({ token, user }) {
  const [data, setData] = useState(null);

  // per-panel sorting state
  const [districtSortBy, setDistrictSortBy] = useState("members"); // "members" | "name"
  const [districtSortDir, setDistrictSortDir] = useState("desc"); // "asc" | "desc"
  const [divisionSortBy, setDivisionSortBy] = useState("members");
  const [divisionSortDir, setDivisionSortDir] = useState("desc");
  const [sectorSortBy, setSectorSortBy] = useState("members");
  const [sectorSortDir, setSectorSortDir] = useState("desc");

  useEffect(() => {
    fetch("https://smartcoreapi.ssfkerala.org/analytics/aggregates.php", {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(setData);
  }, [token]);

  // helper: CSV export for any list
  const exportCSV = (rows, filename = "report") => {
    const headers = ["#", "Name", "Members"];
    const csvRows = rows.map((r, i) => [String(i + 1), r.orgname || "", String(r.members_count || 0)]);
    const csv = [headers, ...csvRows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename.replace(/\s+/g,"_")}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  if(!data) return (
    <div className="bg-white rounded-xl shadow-sm p-8 text-center">
      <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  // Prepare sorted lists
  const sortList = (list = [], by = "members", dir = "desc") => {
    return [...list].sort((a, b) => {
      if (by === "name") {
        const na = (a.orgname || "").toLowerCase();
        const nb = (b.orgname || "").toLowerCase();
        if (na < nb) return dir === "asc" ? -1 : 1;
        if (na > nb) return dir === "asc" ? 1 : -1;
        return 0;
      } else {
        const ma = Number(a.members_count || 0);
        const mb = Number(b.members_count || 0);
        return dir === "asc" ? ma - mb : mb - ma;
      }
    });
  };

  const districtsSorted = data.districts ? sortList(data.districts, districtSortBy, districtSortDir) : null;
  const divisionsSorted = data.divisions ? sortList(data.divisions, divisionSortBy, divisionSortDir) : null;
  const sectorsSorted = data.sectors ? sortList(data.sectors, sectorSortBy, sectorSortDir) : null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {data.districts && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between gap-2">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 px-3 py-2 rounded">
              <h3 className="text-white font-semibold">Districts</h3>
            </div>
            <div className="flex items-center gap-2">
              <select value={districtSortBy} onChange={e => setDistrictSortBy(e.target.value)} className="px-2 py-1 rounded border text-sm">
                <option value="members">Members</option>
                <option value="name">Name</option>
              </select>
              <button onClick={() => setDistrictSortDir(d => d === "asc" ? "desc" : "asc")} className="px-2 py-1 bg-gray-100 rounded text-sm">
                {districtSortDir === "asc" ? "↑" : "↓"}
              </button>
              <button onClick={() => exportCSV(districtsSorted || data.districts, "districts_report")} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
                Export CSV
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {(districtsSorted || data.districts).map(d => (
              <div key={d.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{d.orgname}</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                  {d.members_count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.divisions && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between gap-2">
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 px-3 py-2 rounded">
              <h3 className="text-white font-semibold">Divisions</h3>
            </div>
            <div className="flex items-center gap-2">
              <select value={divisionSortBy} onChange={e => setDivisionSortBy(e.target.value)} className="px-2 py-1 rounded border text-sm">
                <option value="members">Members</option>
                <option value="name">Name</option>
              </select>
              <button onClick={() => setDivisionSortDir(d => d === "asc" ? "desc" : "asc")} className="px-2 py-1 bg-gray-100 rounded text-sm">
                {divisionSortDir === "asc" ? "↑" : "↓"}
              </button>
              <button onClick={() => exportCSV(divisionsSorted || data.divisions, "divisions_report")} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
                Export CSV
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {(divisionsSorted || data.divisions).map(d => (
              <div key={d.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{d.orgname}</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                  {d.members_count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.sectors && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 flex items-center justify-between gap-2">
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 px-3 py-2 rounded">
              <h3 className="text-white font-semibold">Sectors</h3>
            </div>
            <div className="flex items-center gap-2">
              <select value={sectorSortBy} onChange={e => setSectorSortBy(e.target.value)} className="px-2 py-1 rounded border text-sm">
                <option value="members">Members</option>
                <option value="name">Name</option>
              </select>
              <button onClick={() => setSectorSortDir(d => d === "asc" ? "desc" : "asc")} className="px-2 py-1 bg-gray-100 rounded text-sm">
                {sectorSortDir === "asc" ? "↑" : "↓"}
              </button>
              <button onClick={() => exportCSV(sectorsSorted || data.sectors, "sectors_report")} className="px-3 py-1 bg-blue-600 text-white rounded text-sm">
                Export CSV
              </button>
            </div>
          </div>
          <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
            {(sectorsSorted || data.sectors).map(s => (
              <div key={s.id} className="px-4 py-3 hover:bg-gray-50 cursor-pointer transition flex justify-between items-center">
                <span className="text-sm font-medium text-gray-900">{s.orgname}</span>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                  {s.members_count}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}